import {
	IconBriefcase,
	IconCalendar,
	IconClipboardText,
	IconNumber123,
	IconTextCaption,
	IconToggleRight,
	IconTopologyComplex,
	IconUser,
} from "@tabler/icons-react";
import { Space, Typography } from "antd";
import { format } from "date-fns";
import {
	type ArtemisReportDetail,
	type Project,
	ReportStatus,
	type ReportType,
} from "@/common/model";
import { toLabel } from "@/common/utils";
import { IdField } from "../base";
import { BaseDetails, EditableClickable, EditableText } from "../base-details";
import { OwnerField } from "../users/owner-field";
import { ReportStatusBadge, ReportTypeLabelMap } from "./report-badges";

interface ReportDetailsProps {
	report: ArtemisReportDetail;
	onSaveTitle?: (value: string) => void;
	/** Linked projects, already resolved to names by the page. */
	projects: NonNullable<Project>[];
	/** Base path for project links, e.g. `/acme/workspaces/wks_1/projects`. */
	projectsPath: string;
	/** Opens the project edit drawer. Omit to render the links read-only (e.g. when sealed). */
	onEditProjects?: () => void;
	/** Hands ownership to another workspace member. Ignored once sealed, like the title. */
	onSaveOwner?: (userId: string) => void;
}

export function ReportDetails({
	report,
	onSaveTitle,
	projects,
	projectsPath,
	onEditProjects,
	onSaveOwner,
}: ReportDetailsProps) {
	return (
		<BaseDetails
			item={report}
			fields={[
				{
					key: "id",
					tooltip: "ID",
					icon: IconNumber123,
					render: (id) => <IdField id={id as string} showCopyButton />,
				},
				{
					key: "title",
					tooltip: "Title",
					icon: IconClipboardText,
					render: (title) => (
						<EditableText
							onChange={
								onSaveTitle && report.status !== ReportStatus.SEALED
									? onSaveTitle
									: undefined
							}
						>
							{title as string}
						</EditableText>
					),
				},
				{
					key: "type",
					tooltip: "Type",
					icon: IconTopologyComplex,
					render: (type) => ReportTypeLabelMap[type as ReportType],
				},
				{
					key: "status",
					tooltip: "Status",
					icon: IconToggleRight,
					render: (status) => (
						<ReportStatusBadge status={status as ReportStatus} />
					),
				},
				{
					key: "summary",
					tooltip: "Summary",
					icon: IconTextCaption,
					render: (summary) => toLabel(summary),
				},
				{
					key: "projectIds",
					label: "Projects",
					icon: IconBriefcase,
					render: () => {
						const content =
							projects.length === 0 ? (
								<Typography.Text type="secondary">
									{toLabel(null)}
								</Typography.Text>
							) : (
								<Space wrap size={[8, 4]}>
									{projects.map((p) => (
										<IdField
											key={p.id}
											id={p.name}
											link={`${projectsPath}/${p.id}`}
										/>
									))}
								</Space>
							);
						return onEditProjects ? (
							<EditableClickable onClick={onEditProjects}>
								{content}
							</EditableClickable>
						) : (
							content
						);
					},
				},
				{
					key: "ownerId",
					tooltip: "Owner",
					icon: IconUser,
					render: (id) => (
						<OwnerField
							ownerId={id}
							workspaceId={report.workspaceId}
							onSave={
								report.status !== ReportStatus.SEALED ? onSaveOwner : undefined
							}
						/>
					),
				},
				{
					key: "createdAt",
					tooltip: "Created",
					icon: IconCalendar,
					render: (v) => format(new Date(v as string), "MM/dd/yyyy h:mm a"),
				},
				{
					key: "updatedAt",
					tooltip: "Last updated",
					icon: IconCalendar,
					render: (v) => format(new Date(v as string), "MM/dd/yyyy h:mm a"),
				},
			]}
		/>
	);
}
